/**
 * BuildingFieldset - every editable field of a building (location), in one place.
 *
 * Atomic Design role: Molecule. Renders the fields and nothing else — no
 * fetching, no submit, no modal chrome. The caller owns the values, the
 * business-line and customer option lists, and what "save" means.
 *
 * Two surfaces create or edit a building, and before this they were the same
 * form typed twice: Admin → Locations (`renderLocationForm`) and the
 * AddBuildingModal opened from the Buildings page and the job forms. Same ten
 * fields, same grid breakpoints, same customer-assignment box, same copied
 * `inputClass` string — which is exactly the shape a divergence takes hold in.
 * A field added to one of them was a field missing from the other.
 *
 * Differences between the two callers are props, not forks:
 *   - `duplicateWarning` / `onNameBlur` — Admin runs a debounced
 *     "a building with this name already exists" check; the modal doesn't.
 *   - `companyLocked` — opened from a job form the customer is already
 *     decided, so the picker collapses to a confirmation line.
 *
 * Styling note: the fields use the shared form-class tokens
 * (`utils/formClasses`) rather than the `Input` atom, deliberately. The Admin
 * modal renders this fieldset beside the user / company / contact forms, which
 * are built from those same tokens; an atom-styled Buildings tab would be the
 * one form in that modal with different borders and label weight. Converge all
 * four forms onto the atom and this goes with them.
 *
 * @example  // Admin → Locations
 * <BuildingFieldset idPrefix="modal-loc" values={locationForm} onChange={patchLocationForm}
 *     businessLines={businessLines} companies={companyOptions}
 *     nameError={formErrors.name} companyError={formErrors.company_ids}
 *     duplicateWarning={locationDuplicateWarning} onNameBlur={recheckDuplicate} />
 *
 * @example  // inline from a job form — customer already chosen
 * <BuildingFieldset idPrefix="bldg" values={form} onChange={patch}
 *     businessLines={businessLines} companies={companies}
 *     companyLocked lockedCompanyName="GBrands Facility Services" />
 */
import React from 'react';
import { Alert } from '../../atoms';
import { AddressInput } from '../AddressInput';
import { SearchableMultiSelect } from '../SearchableMultiSelect';
import { dedupeAndSort } from '../../../utils/dedupeAndSort';
import { FORM_INPUT_CLASS, FORM_SELECT_CLASS, FORM_LABEL_CLASS } from '../../../utils/formClasses';
import type { BusinessLine } from '../../../services/businessLineService';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/**
 * The editable shape of a building. All strings, including the numeric
 * fields — these are form values, and the caller parses them on submit
 * (`cleanable_sqft` to an int, `tax_rate` to a fraction).
 */
export interface BuildingFormValues {
	name: string;
	address: string;
	/** Suite / unit / dock / gate at this address. */
	address_line_2: string;
	city: string;
	state: string;
	zip: string;
	cleanable_sqft: string;
	tax_rate: string;
	business_line: string;
	notes: string;
	/** Customer (company) ids this building belongs to, as strings. */
	company_ids: string[];
}

/** Customer option for the assignment picker. */
export interface BuildingCustomerOption {
	id: string | number;
	name: string;
}

export interface BuildingFieldsetProps {
	/** Prefix for every input id, so two of these can coexist on one page
	 *  without colliding (`modal-loc-name`, `bldg-name`, …). */
	idPrefix: string;
	/** Current field values. */
	values: BuildingFormValues;
	/** Called with just the changed keys — the caller merges. A patch rather
	 *  than a whole form so a caller can react to *which* field moved (Admin
	 *  re-runs its duplicate check when name or address changes). */
	onChange: (patch: Partial<BuildingFormValues>) => void;
	/** Business lines for the picker. CMS-managed, active-only by convention. */
	businessLines: BusinessLine[];
	/** Customers available for assignment. */
	companies: BuildingCustomerOption[];
	/** Validation message under the name field. */
	nameError?: string;
	/** Validation message inside the customer-assignment box. */
	companyError?: string;
	/** Non-blocking "this may already exist" banner under the name field. */
	duplicateWarning?: React.ReactNode;
	/** Fired when the name field loses focus (Admin re-checks for duplicates,
	 *  which catches a paste-and-tab that never fired a keystroke). */
	onNameBlur?: () => void;
	/** Collapses the customer picker to a confirmation line. Used when the
	 *  building must land under a customer that was already chosen elsewhere. */
	companyLocked?: boolean;
	/** Name shown in that confirmation line. */
	lockedCompanyName?: string | null;
	/** Copy under the "Assign to Customers" heading. */
	customersHint?: React.ReactNode;
	/** Copy shown when there are no customers to assign. */
	emptyCustomersText?: string;
	/** Extra classes merged onto the root. */
	className?: string;
	/** Overrides the molecule's data-ref so a composing component keeps its identity. */
	dataRef?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const BuildingFieldset: React.FC<BuildingFieldsetProps> = ({
	idPrefix,
	values,
	onChange,
	businessLines,
	companies,
	nameError,
	companyError,
	duplicateWarning,
	onNameBlur,
	companyLocked = false,
	lockedCompanyName,
	customersHint = 'Customers are the companies you bill. Manage them in Admin → Companies.',
	emptyCustomersText = 'No customers available.',
	className = '',
	dataRef = 'molecule-building-fieldset',
}) => {
	/** Customer options for the picker — deduped by name and sorted A-Z. */
	const customerOptions = dedupeAndSort(companies.map((co) => ({ value: String(co.id), label: co.name })));

	return (
		<div className={`building-fieldset space-y-12px ${className}`} data-ref={dataRef}>
			{/* Building Name (required) */}
			<div className="building-fieldset__field">
				<label htmlFor={`${idPrefix}-name`} className={FORM_LABEL_CLASS}>
					Building Name <span className="text-red-500">*</span>
				</label>
				<input
					type="text"
					id={`${idPrefix}-name`}
					value={values.name}
					onChange={(e) => onChange({ name: e.target.value })}
					onBlur={onNameBlur}
					className={`${FORM_INPUT_CLASS} ${nameError ? 'border-red-500' : ''}`}
					placeholder="Downtown Office Tower"
				/>
				{nameError && <p className="building-fieldset__error mt-4px text-12px text-red-600">{nameError}</p>}
				{/* Duplicate warning — amber and non-blocking; a second building at
				    the same address is unusual but legitimate. */}
				{duplicateWarning && <Alert variant="warning" size="sm" className="mt-4px">{duplicateWarning}</Alert>}
			</div>

			{/* Address — Google Places autocomplete; selecting a suggestion fills
			    city / state / ZIP below, all of which stay editable. */}
			<div className="building-fieldset__field">
				<label htmlFor={`${idPrefix}-address`} className={FORM_LABEL_CLASS}>Address</label>
				<AddressInput
					id={`${idPrefix}-address`}
					value={values.address}
					onChange={(val) => onChange({ address: val })}
					onSelect={(parsed) => onChange({ address: parsed.street, city: parsed.city, state: parsed.state, zip: parsed.zip })}
					placeholder="Start typing address..."
				/>
			</div>

			{/* Address Line 2 — suite, unit, dock, gate */}
			<div className="building-fieldset__field">
				<label htmlFor={`${idPrefix}-address-line-2`} className={FORM_LABEL_CLASS}>Address Line 2</label>
				<input type="text" id={`${idPrefix}-address-line-2`} value={values.address_line_2} onChange={(e) => onChange({ address_line_2: e.target.value })} className={FORM_INPUT_CLASS} placeholder="Suite 100, Dock 4, Gate B..." />
			</div>

			{/* City / State / ZIP — one per line on a phone, one row from tablet up */}
			<div className="building-fieldset__row grid grid-cols-1 tablet:grid-cols-3 gap-12px">
				<div className="building-fieldset__field">
					<label htmlFor={`${idPrefix}-city`} className={FORM_LABEL_CLASS}>City</label>
					<input type="text" id={`${idPrefix}-city`} value={values.city} onChange={(e) => onChange({ city: e.target.value })} className={FORM_INPUT_CLASS} placeholder="Indianapolis" />
				</div>
				<div className="building-fieldset__field">
					<label htmlFor={`${idPrefix}-state`} className={FORM_LABEL_CLASS}>State</label>
					<input type="text" id={`${idPrefix}-state`} value={values.state} onChange={(e) => onChange({ state: e.target.value })} className={FORM_INPUT_CLASS} placeholder="IN" maxLength={2} />
				</div>
				<div className="building-fieldset__field">
					<label htmlFor={`${idPrefix}-zip`} className={FORM_LABEL_CLASS}>ZIP</label>
					<input type="text" id={`${idPrefix}-zip`} value={values.zip} onChange={(e) => onChange({ zip: e.target.value })} className={FORM_INPUT_CLASS} placeholder="46204" maxLength={10} />
				</div>
			</div>

			{/* Cleanable Sq Ft + Tax Rate */}
			<div className="building-fieldset__row grid grid-cols-1 tablet:grid-cols-2 gap-12px">
				<div className="building-fieldset__field">
					<label htmlFor={`${idPrefix}-sqft`} className={FORM_LABEL_CLASS}>Cleanable Sq Ft</label>
					<input type="number" id={`${idPrefix}-sqft`} value={values.cleanable_sqft} onChange={(e) => onChange({ cleanable_sqft: e.target.value })} className={FORM_INPUT_CLASS} placeholder="50000" min="0" />
				</div>
				<div className="building-fieldset__field">
					<label htmlFor={`${idPrefix}-tax-rate`} className={FORM_LABEL_CLASS}>Tax Rate (%)</label>
					<input type="number" id={`${idPrefix}-tax-rate`} value={values.tax_rate} onChange={(e) => onChange({ tax_rate: e.target.value })} className={FORM_INPUT_CLASS} placeholder="7.0" step="0.01" min="0" max="100" />
				</div>
			</div>

			{/* Business Line — CMS-managed list */}
			<div className="building-fieldset__field">
				<label htmlFor={`${idPrefix}-business-line`} className={FORM_LABEL_CLASS}>Business Line</label>
				<select id={`${idPrefix}-business-line`} value={values.business_line} onChange={(e) => onChange({ business_line: e.target.value })} className={FORM_SELECT_CLASS}>
					<option value="">— Any —</option>
					{businessLines.map((bl) => (
						<option key={bl.id} value={bl.slug}>{bl.label}</option>
					))}
				</select>
			</div>

			{/* Notes */}
			<div className="building-fieldset__field">
				<label htmlFor={`${idPrefix}-notes`} className={FORM_LABEL_CLASS}>Notes</label>
				<textarea id={`${idPrefix}-notes`} value={values.notes} onChange={(e) => onChange({ notes: e.target.value })} className={FORM_INPUT_CLASS} rows={3} placeholder="Special access instructions or notes..." />
			</div>

			{/* Customer assignment.
				"Customers" rather than "companies": the Companies tab is the customer
				database (billing address, terms, tax rate), and linking a building to
				a customer is what routes its work orders to the right billing account.
				A building with no customer link never appears in the Building dropdown
				on the job forms, which is why this is required.

				The picker is the shared search-and-add control rather than a scrolling
				checkbox list: an account with more customers than fit the box turns
				"tick the right one" into a scroll hunt for a name you could have typed
				three letters of. */}
			<div className="building-fieldset__customers border border-brand-border rounded-md p-12px">
				<p className="building-fieldset__customers-label text-12px font-medium text-brand-text-muted uppercase tracking-wide mb-4px">
					Assign to Customers <span className="text-red-500">*</span>
				</p>

				{companyLocked ? (
					<p className="text-12px text-brand-text-muted">
						This building will be added to <span className="font-semibold text-brand-text">{lockedCompanyName ?? 'the selected customer'}</span>.
					</p>
				) : (
					<>
						{customersHint && <p className="text-12px text-brand-text-subtle mb-8px">{customersHint}</p>}
						{companyError && <p className="building-fieldset__error text-12px text-status-error mb-8px">{companyError}</p>}
						{customerOptions.length === 0 ? (
							<p className="text-12px text-brand-text-subtle italic">{emptyCustomersText}</p>
						) : (
							<SearchableMultiSelect
								id={`${idPrefix}-customers`}
								options={customerOptions}
								selectedValues={values.company_ids}
								onChange={(next) => onChange({ company_ids: next })}
								placeholder="Search customers..."
								emptyText="No customers yet — search above to add one."
								allSelectedText="Every customer is already assigned to this building."
								hasError={Boolean(companyError)}
								dataRef="molecule-building-fieldset-customers"
							/>
						)}
					</>
				)}
			</div>
		</div>
	);
};

export default BuildingFieldset;
